import { useState, useEffect, useRef } from "react";
import { createFarm } from "../../../../../api/farm-service/farmService";
import {
  initiateImageUpload,
  confirmCoverImageUpload,
  confirmGalleryUpload,
} from "../../../../../api/farm-service/imageService";
import { getCategories } from "../../../../../api/farm-service/categoryService";
import { getFeatures } from "../../../../../api/farm-service/featureService";
import "../../../../../assets/stylesheets/Listing.css";
import DashboardLayout from "../../../DashboardLayout";

const initialHours = {
  Mon: { open: "10:00", close: "21:00", closed: false },
  Tue: { open: "10:00", close: "21:00", closed: false },
  Wed: { open: "10:00", close: "21:00", closed: false },
  Thu: { open: "10:00", close: "21:00", closed: false },
  Fri: { open: "10:00", close: "21:00", closed: false },
  Sat: { open: "10:00", close: "21:00", closed: false },
  Sun: { open: "10:00", close: "21:00", closed: false },
};

const SOURCE_FILE_LIMIT_MB = 15;
const SOURCE_FILE_LIMIT_BYTES = SOURCE_FILE_LIMIT_MB * 1024 * 1024;

const TARGET_UPLOAD_BYTES = 1 * 1024 * 1024;
const MAX_PROCESS_DIMENSION = 2048;
const MIN_PROCESS_DIMENSION = 640;
const INITIAL_WEBP_QUALITY = 0.8;
const MIN_WEBP_QUALITY = 0.5;
const WEBP_QUALITY_STEP = 0.1;
const SCALE_STEP = 0.85;

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const requiredAddressLabels = {
  street1: "Street Address",
  street2: "Address Line 2",
  city: "City",
  region: "Region",
  state: "Province",
  zipcode: "Zip Code",
  country: "Country",
};

const optionalAddressKeys = new Set(["street2", "region"]);

function cloneInitialHours() {
  return Object.fromEntries(
    Object.entries(initialHours).map(([day, values]) => [day, { ...values }])
  );
}

function createInitialFormState() {
  return {
    name: "",
    description: "",
    phoneNumber: "",
    webUrl: "",
    socialUrl: "",
    banner: "",
    tips: [{ tipTitle: "", tipText: "" }],
    categoryIds: [],
    featureIds: [],
    coverImage: null,
    coverPreview: "",
    images: [],
    imagePreviews: [],
    address: {
      street1: "",
      street2: "",
      city: "",
      region: "",
      state: "Ontario",
      zipcode: "",
      country: "Canada",
    },
    businessHours: cloneInitialHours(),
  };
}

function isValidUrl(value) {
  if (!value) return true;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function formatPhoneNumber(digits) {
  if (!digits) return "";
  let display = "+1";
  if (digits.length >= 1) display += " (" + digits.slice(0, 3);
  if (digits.length >= 3) display += ")";
  if (digits.length > 3) display += " " + digits.slice(3, 6);
  if (digits.length > 6) display += " " + digits.slice(6, 10);
  return display;
}

async function canvasToWebPFile(file, options = {}) {
  const targetBytes = options.targetBytes ?? TARGET_UPLOAD_BYTES;
  const maxDimension = options.maxDimension ?? MAX_PROCESS_DIMENSION;
  const minDimension = options.minDimension ?? MIN_PROCESS_DIMENSION;
  const initialQuality = options.quality ?? INITIAL_WEBP_QUALITY;
  const minQuality = options.minQuality ?? MIN_WEBP_QUALITY;

  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Image load failed"));
    };
    img.src = objectUrl;
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas context unavailable");
  }

  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  const naturalMaxDimension = Math.max(naturalWidth, naturalHeight);

  let scale = naturalMaxDimension > 0 ? Math.min(1, maxDimension / naturalMaxDimension) : 1;
  let quality = initialQuality;
  let bestBlob = null;

  const renderBlob = () =>
    new Promise((resolve, reject) => {
      const width = Math.max(1, Math.round(naturalWidth * scale));
      const height = Math.max(1, Math.round(naturalHeight * scale));
      canvas.width = width;
      canvas.height = height;
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(image, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Canvas conversion failed"));
          }
        },
        "image/webp",
        quality
      );
    });

  while (true) {
    const blob = await renderBlob();
    if (!bestBlob || blob.size < bestBlob.size) {
      bestBlob = blob;
    }

    if (blob.size <= targetBytes) {
      const fileName = file.name ? file.name.replace(/\.\w+$/, ".webp") : "image.webp";
      return new File([blob], fileName, { type: "image/webp" });
    }

    if (quality - minQuality > 1e-3) {
      quality = Math.max(minQuality, quality - WEBP_QUALITY_STEP);
      continue;
    }

    const currentMaxDimension = Math.max(canvas.width, canvas.height);
    if (currentMaxDimension * SCALE_STEP >= minDimension) {
      scale *= SCALE_STEP;
      continue;
    }

    break;
  }

  if (bestBlob) {
    const fileName = file.name ? file.name.replace(/\.\w+$/, ".webp") : "image.webp";
    if (bestBlob.size > targetBytes) {
      console.warn(
        `Processed image remains above target size: ${(bestBlob.size / (1024 * 1024)).toFixed(2)}MB`
      );
    }
    return new File([bestBlob], fileName, { type: "image/webp" });
  }

  throw new Error("Canvas conversion failed");
}

function validateTip(tip) {
  const errors = {};
  if (!tip.tipTitle?.trim()) {
    errors.tipTitle = "Tip title cannot be empty";
  } else if (tip.tipTitle.length < 2) {
    errors.tipTitle = "Tip title must be at least 2 characters long";
  } else if (tip.tipTitle.length > 100) {
    errors.tipTitle = "Tip title cannot exceed 100 characters";
  }

  if (!tip.tipText?.trim()) {
    errors.tipText = "Tip text cannot be empty";
  } else if (tip.tipText.length > 300) {
    errors.tipText = "Tip text cannot exceed 250 characters";
  }

  return errors;
}

function validateCategoryOrFeature(item) {
  const errors = {};
  if (!item.name?.trim()) {
    errors.name = "Name cannot be empty";
  } else if (item.name.length < 2) {
    errors.name = "Name must be at least 2 characters long";
  } else if (item.name.length > 100) {
    errors.name = "Name cannot exceed 100 characters";
  }

  if (item.description && item.description.length > 1000) {
    errors.description = "Description cannot exceed 1000 characters";
  }

  return errors;
}

export default function FarmListing() {
  const [allCategories, setAllCategories] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [form, setForm] = useState(createInitialFormState);
  const [rawPhone, setRawPhone] = useState("");
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  const formattedPhone = formatPhoneNumber(rawPhone);
  const isBusy = isProcessingImages || isSubmitting;

  useEffect(() => {
    async function fetchLookups() {
      try {
        const [categories, features] = await Promise.all([
          getCategories(),
          getFeatures(),
        ]);
        setAllCategories(categories.data ?? []);
        setAllFeatures(features.data ?? []);
      } catch (error) {
        console.error("Error loading lookups", error);
      }
    }

    fetchLookups();
  }, []);

  const removeCoverImage = () => {
    setForm((prev) => ({
      ...prev,
      coverImage: null,
      coverPreview: "",
    }));
    if (coverInputRef.current) {
      coverInputRef.current.value = "";
    }
  };

  const removeGalleryImage = (index) => {
    setForm((prev) => {
      const images = prev.images.filter((_, i) => i !== index);
      const previews = prev.imagePreviews.filter((_, i) => i !== index);
      return {
        ...prev,
        images,
        imagePreviews: previews,
      };
    });
    if (galleryInputRef.current) {
      galleryInputRef.current.value = "";
    }
  };

  const handleCheckbox = (field, value) => {
    setForm((prev) => {
      const next = new Set(prev[field]);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return { ...prev, [field]: Array.from(next) };
    });
  };

  const handleCoverImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > SOURCE_FILE_LIMIT_BYTES) {
      alert(`Image must be under ${SOURCE_FILE_LIMIT_MB}MB before optimization.`);
      if (coverInputRef.current) coverInputRef.current.value = "";
      return;
    }

    setIsProcessingImages(true);
    try {
      const optimized = await canvasToWebPFile(file);
      if (optimized.size > TARGET_UPLOAD_BYTES) {
        alert("Unable to optimize cover image below 1MB. Please choose a smaller image.");
        if (coverInputRef.current) coverInputRef.current.value = "";
        return;
      }

      const preview = URL.createObjectURL(optimized);
      setForm((prev) => ({
        ...prev,
        coverImage: optimized,
        coverPreview: preview,
      }));
    } catch (error) {
      console.error("Cover image optimization failed", error);
      alert("Cover image optimization failed. Please try a different file.");
      if (coverInputRef.current) coverInputRef.current.value = "";
    } finally {
      setIsProcessingImages(false);
    }
  };

  const handleImages = async (event) => {
    const newFiles = Array.from(event.target.files ?? []);
    if (newFiles.length === 0) return;

    if (form.images.length + newFiles.length < 4) {
      alert("You must upload at least 4 images.");
      return;
    }

    setIsProcessingImages(true);
    const images = [...form.images];
    const previews = [...form.imagePreviews];
    let added = false;

    try {
      for (const file of newFiles) {
        if (file.size > SOURCE_FILE_LIMIT_BYTES) {
          alert(`${file.name} exceeds ${SOURCE_FILE_LIMIT_MB}MB before optimization.`);
          continue;
        }

        try {
          const optimized = await canvasToWebPFile(file);
          if (optimized.size > TARGET_UPLOAD_BYTES) {
            alert(`${file.name} could not be optimized below 1MB. Please choose another image.`);
            continue;
          }

          images.push(optimized);
          previews.push(URL.createObjectURL(optimized));
          added = true;
        } catch (error) {
          console.error(`Gallery optimization failed for ${file.name}`, error);
          alert(`${file.name} optimization failed. Please try another image.`);
        }
      }
    } finally {
      setIsProcessingImages(false);
      if (galleryInputRef.current) {
        galleryInputRef.current.value = "";
      }
    }

    if (added) {
      setForm((prev) => ({ ...prev, images, imagePreviews: previews }));
    }
  };

  const handleHourChange = (day, key, value) => {
    setForm((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: {
          ...prev.businessHours[day],
          [key]: value,
          closed: false,
        },
      },
    }));
  };

  const toggleClosed = (day) => {
    setForm((prev) => ({
      ...prev,
      businessHours: {
        ...prev.businessHours,
        [day]: prev.businessHours[day].closed
          ? { ...initialHours[day] }
          : { closed: true, open: null, close: null },
      },
    }));
  };

  const handleTipsChange = (index, field, value) => {
    setForm((prev) => {
      const tips = [...prev.tips];
      tips[index] = { ...tips[index], [field]: value };
      return { ...prev, tips };
    });
  };

  const addTip = () => {
    setForm((prev) => {
      if (prev.tips.length >= 10) return prev;
      return { ...prev, tips: [...prev.tips, { tipTitle: "", tipText: "" }] };
    });
  };

  const removeTip = (index) => {
    setForm((prev) => ({
      ...prev,
      tips: prev.tips.filter((_, i) => i !== index),
    }));
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value;
    const digits = value.replace(/\D/g, "");
    const normalized = (digits.startsWith("1") ? digits.slice(1) : digits).slice(0, 10);
    setRawPhone(normalized);
    const formatted = formatPhoneNumber(normalized);
    setForm((prev) => ({ ...prev, phoneNumber: formatted }));

    queueMicrotask(() => {
      if (!phoneInputRef.current) return;
      const target = phoneInputRef.current;
      const digitsBeforeCursor = (target.selectionStart ?? value.length)
        ? value.slice(0, target.selectionStart ?? value.length).replace(/\D/g, "").length
        : 0;

      let cursorPosition = formatted.length;
      let seenDigits = 0;
      for (let i = 0; i < formatted.length; i += 1) {
        if (/\d/.test(formatted[i])) {
          seenDigits += 1;
        }
        if (seenDigits === digitsBeforeCursor) {
          cursorPosition = i + 1;
          break;
        }
      }

      target.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting || isProcessingImages) return;

    const {
      name,
      description,
      tips,
      categoryIds,
      featureIds,
      coverImage,
      images,
      businessHours,
      address,
    } = form;

    const missingFields = [];

    if (!name.trim()) missingFields.push("Farm Name");
    if (!description.trim()) missingFields.push("Description");

    if (tips.length === 0) {
      missingFields.push("At least one tip is required");
    } else {
      tips.forEach((tip, index) => {
        const errors = validateTip(tip);
        Object.entries(errors).forEach(([field, message]) => {
          missingFields.push(`Tip #${index + 1} ${field}: ${message}`);
        });
      });
    }

    if (categoryIds.length === 0) {
      missingFields.push("At least one category is required");
    }
    if (featureIds.length === 0) {
      missingFields.push("At least one feature is required");
    }

    categoryIds.forEach((id) => {
      const category = allCategories.find((cat) => cat.id === id);
      if (category) {
        const errors = validateCategoryOrFeature(category);
        Object.entries(errors).forEach(([field, message]) => {
          missingFields.push(`Category '${category.name}' ${field}: ${message}`);
        });
      }
    });

    featureIds.forEach((id) => {
      const feature = allFeatures.find((item) => item.id === id);
      if (feature) {
        const errors = validateCategoryOrFeature(feature);
        Object.entries(errors).forEach(([field, message]) => {
          missingFields.push(`Feature '${feature.name}' ${field}: ${message}`);
        });
      }
    });

    if (!coverImage) missingFields.push("Cover Image");
    if (images.length === 0) missingFields.push("Gallery Images");

    if (rawPhone.length < 10) {
      missingFields.push("Phone Number");
    }
    if (!isValidUrl(form.webUrl)) missingFields.push("Web URL");
    if (!isValidUrl(form.socialUrl)) missingFields.push("Social URL");

    Object.entries(businessHours).forEach(([day, data]) => {
      if (!data.closed && (!data.open || !data.close)) {
        missingFields.push(`Business Hours: ${day} is missing open/close time`);
      }
    });

    Object.entries(requiredAddressLabels).forEach(([key, label]) => {
      if (!optionalAddressKeys.has(key) && !address[key]?.trim()) {
        missingFields.push(`Address: ${label}`);
      }
    });

    if (missingFields.length > 0) {
      alert(`Please complete the following required fields:\n\n- ${missingFields.join("\n- ")}`);
      return;
    }

    const businessHoursDto = DAYS.map((day, idx) => ({
      dayOfWeek: idx,
      openTime: businessHours[day].open,
      closeTime: businessHours[day].close,
    }));

    const tipsDto = tips.map((tip) => ({
      tipTitle: tip.tipTitle,
      tipText: tip.tipText,
    }));

    const locationDto = {
      street1: address.street1,
      street2: address.street2,
      city: address.city,
      region: address.region,
      state: address.state,
      zipCode: address.zipcode,
      country: address.country,
    };

    const payload = {
      farm: {
        name,
        description,
        phoneNumber: formattedPhone,
        webUrl: form.webUrl || null,
        socialUrl: form.socialUrl || null,
        banner: form.banner,
        businessHours: businessHoursDto,
        tips: tipsDto,
        categoryIds,
        featureIds,
      },
      location: locationDto,
    };

    setIsSubmitting(true);
    try {
      const result = await createFarm(payload);
      const farmId = result?.data?.id;

      if (!farmId) {
        throw new Error("Missing farm id in create response");
      }

      const {
        data: { uploadUrl: coverUploadUrl, id: coverId },
      } = await initiateImageUpload(farmId, {
        fileName: form.coverImage.name,
        fileType: form.coverImage.type,
        domain: "farm",
        ownerId: farmId,
      });

      await fetch(coverUploadUrl, {
        method: "PUT",
        headers: { "Content-Type": form.coverImage.type },
        body: form.coverImage,
      });

      await confirmCoverImageUpload(farmId, coverId);

      const galleryIds = [];
      for (const file of form.images) {
        const {
          data: { uploadUrl, id },
        } = await initiateImageUpload(farmId, {
          fileName: file.name,
          fileType: file.type,
          domain: "farm",
          ownerId: farmId,
        });

        await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        galleryIds.push(id);
      }

      await confirmGalleryUpload(farmId, galleryIds);

      alert("Farm created successfully!");
      setForm(createInitialFormState());
      setRawPhone("");
      if (coverInputRef.current) coverInputRef.current.value = "";
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    } catch (error) {
      console.error("Submit error", error);
      alert("Error creating farm. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <form
        autoComplete="off"
        className={`listing-form ${isBusy ? "is-busy" : ""}`}
        onSubmit={handleSubmit}
      >
        <div className="form-header">
          <h2>Register a Farm</h2>
          <p className="form-subtitle">
            Share the essentials so visitors can discover and plan their visit.
          </p>
        </div>

        {(isProcessingImages || isSubmitting) && (
          <div
            className={`form-status-banner ${isSubmitting ? "status-submitting" : "status-processing"}`}
            role="status"
            aria-live="polite"
          >
            <span className="spinner" aria-hidden="true" />
            <span>{isSubmitting ? "Submitting farm…" : "Optimizing images…"}</span>
          </div>
        )}

        <div className="form-section">
          <h3 className="section-title">Farm Basics</h3>
          <label className="field-label">
            <span>*Farm Name (2-100 characters)</span>
            <input
              name="name"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              maxLength={100}
              required
            />
          </label>

          <label className="field-label">
            <span>*Description (max 2000 characters)</span>
            <textarea
              name="description"
              value={form.description}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, description: event.target.value }))
              }
              maxLength={2000}
            />
          </label>

          <label className="field-label">
            <span>Banner (max 50 characters)</span>
            <select
              name="banner"
              value={form.banner}
              onChange={(event) => setForm((prev) => ({ ...prev, banner: event.target.value }))}
            >
              <option value="">Select Banner</option>
              <option value="New">New</option>
              <option value="Staff Pick">Staff Pick</option>
              <option value="Trending">Trending</option>
            </select>
          </label>
        </div>

        <div className="form-section">
          <h3 className="section-title">Contact &amp; Links</h3>
          <label className="field-label">
            <span>*Phone Number</span>
            <input
              ref={phoneInputRef}
              type="text"
              name="phoneNumber"
              value={formattedPhone}
              onChange={handlePhoneChange}
              placeholder="+1 (___) ___ ____"
              maxLength={18}
            />
          </label>

          <label className="field-label">
            <span>Web URL (max 255 characters)</span>
            <input
              type="url"
              name="webUrl"
              value={form.webUrl}
              onChange={(event) => setForm((prev) => ({ ...prev, webUrl: event.target.value }))}
              maxLength={255}
            />
          </label>

          <label className="field-label">
            <span>Social URL (max 255 characters)</span>
            <input
              type="url"
              name="socialUrl"
              value={form.socialUrl}
              onChange={(event) => setForm((prev) => ({ ...prev, socialUrl: event.target.value }))}
              maxLength={255}
            />
          </label>
        </div>

        <div className="form-section">
          <h3 className="section-title">Visitor Tips</h3>
          <div className="tips-wrapper">
            {form.tips.map((tip, index) => (
              <div key={index} className="tip-card">
                <input
                  type="text"
                  placeholder="Tip Title (2-100 chars)"
                  value={tip.tipTitle}
                  onChange={(event) => handleTipsChange(index, "tipTitle", event.target.value)}
                  maxLength={100}
                />
                <textarea
                  placeholder="Tip Text (max 250 chars)"
                  value={tip.tipText}
                  onChange={(event) => handleTipsChange(index, "tipText", event.target.value)}
                  maxLength={250}
                />
                <button type="button" onClick={() => removeTip(index)} className="remove-tip">
                  Remove
                </button>
              </div>
            ))}
            {form.tips.length < 10 && (
              <button type="button" onClick={addTip} className="add-tip">
                + Add Tip
              </button>
            )}
          </div>
        </div>

        <div className="form-section form-split">
          <div className="form-panel">
            <fieldset className="form-lists">
              <legend>*Categories (max 3)</legend>
              {form.categoryIds.length >= 3 && (
                <p className="validation-message">Maximum of 3 categories allowed</p>
              )}
              {allCategories.map((category) => (
                <label
                  key={category.id}
                  className={
                    form.categoryIds.length >= 3 && !form.categoryIds.includes(category.id)
                      ? "disabled"
                      : ""
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.categoryIds.includes(category.id)}
                    onChange={() => handleCheckbox("categoryIds", category.id)}
                    disabled={isSubmitting && !form.categoryIds.includes(category.id)}
                  />
                  {category.name}
                </label>
              ))}
            </fieldset>
          </div>
          <div className="form-panel">
            <fieldset className="form-lists">
              <legend>*Features (max 8)</legend>
              {form.featureIds.length >= 8 && (
                <p className="validation-message">Maximum of 8 features allowed</p>
              )}
              {allFeatures.map((feature) => (
                <label
                  key={feature.id}
                  className={
                    form.featureIds.length >= 8 && !form.featureIds.includes(feature.id)
                      ? "disabled"
                      : ""
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.featureIds.includes(feature.id)}
                    onChange={() => handleCheckbox("featureIds", feature.id)}
                    disabled={isSubmitting && !form.featureIds.includes(feature.id)}
                  />
                  {feature.name}
                </label>
              ))}
            </fieldset>
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Media</h3>
          <p className="section-helper">We automatically optimize large files so they upload smoothly.</p>
          <label className="field-label">
            <span>*Cover Image (auto-optimized, source ≤{SOURCE_FILE_LIMIT_MB}MB)</span>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverImage}
              disabled={isBusy}
            />
          </label>
          {form.coverPreview && (
            <div className="image-preview-wrapper">
              <div className="image-wrapper">
                <img src={form.coverPreview} alt="Cover Preview" />
                <button type="button" className="remove-image-btn" onClick={removeCoverImage}>
                  ×
                </button>
              </div>
            </div>
          )}

          <label className="field-label">
            <span>*Gallery Images (min 4, optimized ≤1MB each)</span>
            <input
              ref={galleryInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleImages}
              disabled={isBusy}
            />
          </label>
          {form.imagePreviews.length > 0 && (
            <div className="image-preview-wrapper">
              {form.imagePreviews.map((src, index) => (
                <div key={index} className="image-wrapper">
                  <img src={src} alt={`Gallery Preview ${index + 1}`} />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeGalleryImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {isProcessingImages && (
            <div className="image-processing-indicator" role="status" aria-live="polite">
              <span className="spinner" aria-hidden="true" />
              <span>Optimizing images…</span>
            </div>
          )}
        </div>

        <div className="form-section">
          <h3 className="section-title">Business Hours</h3>
          <fieldset className="hours-grid">
            {DAYS.map((day) => {
              const data = form.businessHours[day];
              return (
                <div key={day} className="day-row">
                  <strong>{day}</strong>
                  <input
                    type="time"
                    value={data.open || ""}
                    disabled={data.closed}
                    onChange={(event) => handleHourChange(day, "open", event.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="time"
                    value={data.close || ""}
                    disabled={data.closed}
                    onChange={(event) => handleHourChange(day, "close", event.target.value)}
                  />
                  <label className="closed-toggle">
                    <input
                      type="checkbox"
                      checked={data.closed}
                      onChange={() => toggleClosed(day)}
                    />
                    Closed
                  </label>
                </div>
              );
            })}
          </fieldset>
        </div>

        <div className="form-section">
          <h3 className="section-title">Location</h3>
          <fieldset className="address">
            <label className="field-label">
              <span>*Address</span>
              <div className="address-grid">
                {Object.entries(form.address).map(([key, value]) => (
                  <input
                    key={key}
                    name={key}
                    placeholder={`${requiredAddressLabels[key]}${
                      optionalAddressKeys.has(key) ? " (optional)" : ""
                    }`}
                    value={value}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        address: { ...prev.address, [key]: event.target.value },
                      }))
                    }
                  />
                ))}
              </div>
            </label>
          </fieldset>
        </div>

        <div className="form-footer">
          <button type="submit" className="submit-btn" disabled={isBusy}>
            {isSubmitting ? (
              <>
                <span className="spinner" aria-hidden="true" />
                <span>Submitting…</span>
              </>
            ) : (
              "Submit Farm Application"
            )}
          </button>
          <p className="submit-hint">Uploads may take a few seconds depending on image sizes.</p>
        </div>

        {isSubmitting && (
          <div className="form-overlay" aria-hidden="true">
            <div className="overlay-card">
              <span className="spinner large" aria-hidden="true" />
              <p>Submitting your farm…</p>
              <p className="muted">Please stay on this page while we finish up.</p>
            </div>
          </div>
        )}
      </form>
    </DashboardLayout>
  );
}